import { inject, Injectable } from '@angular/core';
import { OauthService, OauthState } from '../oauth/oauth.service';
import { Router } from '@angular/router';
import { ConfigurationService } from '../configuration/configuration.service';
import { HttpClient } from '@angular/common/http';
import { Observable, Subscriber } from 'rxjs';
import { environment } from '../../../environments/environment';
import { NGXLogger } from 'ngx-logger';
import { finalize, map } from 'rxjs/operators';
import { EmailService } from './email.service';
import { ProductsService } from './products.service';
import { Address, AddressService } from './address.service';
import { PhoneNumbers, PhoneService } from './phone.service';
import { Profile, ProfileService } from './profile.service';
import { MLoginPaymentDetails, MLoginPaymentDetailsService } from './m-login-payment-details.service';
import { CustomerDataStorageService } from './customer-data-storage.service';
import { MLoginIdService } from './m-login-id.service';
import { AUTHORIZATION_ENDPOINT, USER_INFO_ENDPOINT } from '../core-api.constants';
import { Nullable } from '../../shared/shared.types';

const DUMMY_EMAIL = 'test@swm-more.de';
const DUMMY_PRODUCTS = ['PARTNERKRAFT'];

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly oauthService = inject(OauthService);
  private readonly mLoginIdService = inject(MLoginIdService);
  private readonly emailService = inject(EmailService);
  private readonly addressService = inject(AddressService);
  private readonly phoneService = inject(PhoneService);
  private readonly profileService = inject(ProfileService);
  private readonly productsService = inject(ProductsService);
  private readonly paymentService = inject(MLoginPaymentDetailsService);
  private readonly customerDataStorageService = inject(
    CustomerDataStorageService,
  );
  private readonly configurationService = inject(ConfigurationService);
  private readonly logger = inject(NGXLogger);
  private readonly resource: string = `${this.configurationService.apiUrl}${AUTHORIZATION_ENDPOINT}`;

  private handleError(error: any) {
    this.logger.error('Login fehlgeschlagen', error);
    this.router.navigateByUrl('/login-failed', {
      replaceUrl: true,
    });
  }

  public login(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.oauthService.handleRedirect().subscribe(
        (result) => {
          if (result) {
            this.logger.debug('OAUTH Callback-Parameter gefunden', result);
            this.doLogin(
              result.code || '',
              result.codeVerifier || '',
              result.redirectUri || '',
            ).subscribe(
              (response) => {
                this.logger.debug('Login erfolgreich');
                this.saveUserInfo(response);
                this.navigateToProductHomeOnLoginSuccess(result);
                observer.next(true);
                observer.complete();
              },
              (error) => this.handleError(error),
            );
          } else {
            this.logger.debug('OAUTH Authentifizierung erforderlich');
            this.clearCredentials(location.pathname + location.search);
            observer.next(false);
            observer.complete();
          }
        },
        (error) => this.handleError(error),
      );
    });
  }

  public getUserInfo(): Observable<AuthorizationResponse> {
    return this.http
      .get<AuthorizationResponse>(
        `${this.configurationService.apiUrl}${USER_INFO_ENDPOINT}`,
      )
      .pipe(
        map((response) => {
          this.saveUserInfo(response);
          return response;
        }),
      );
  }

  private saveUserInfo(response: AuthorizationResponse): void {
    this.mLoginIdService.set(response.mLoginId);
    this.emailService.set(response.emailAddress);
    this.addressService.set(response.address);
    this.productsService.set(response.products);
    this.phoneService.set(new PhoneNumbers(response.line, response.mobile));
    this.profileService.set(response.profile);
    this.paymentService.set(response.payment);
  }

  public logout() {
    this.doLogout()
      .pipe(finalize(() => this.clearCredentials(location.pathname)))
      .subscribe();
  }

  public clearCredentials(redirectTo?: string) {
    if (environment.production) {
      this.mLoginIdService.remove();
      this.emailService.remove();
      this.productsService.remove();
      this.addressService.remove();
      this.phoneService.remove();
      this.profileService.remove();
      this.paymentService.remove();
      this.customerDataStorageService.remove();
      this.oauthService.initAuthorizationCodeFlow({
        requestUrl: redirectTo ? redirectTo : '',
      });
    } else {
      this.emailService.set(DUMMY_EMAIL);
      this.productsService.set(DUMMY_PRODUCTS);
      location.reload();
    }
  }

  public registerProduct(productType: string, pin: string): Observable<any> {
    return new Observable<any>((observer) => {
      this.doUpdate(productType, pin).subscribe((response) => {
        this.saveUserInfo(response);
        observer.next(true);
        observer.complete();
      });
    });
  }

  get mLoginId(): string {
    return this.mLoginIdService.get();
  }

  get email(): string {
    return this.emailService.get();
  }

  get address(): Address {
    return this.addressService.get();
  }

  get phoneNumbers(): PhoneNumbers {
    return this.phoneService.get();
  }

  get profile(): Profile {
    return this.profileService.get();
  }

  get payment(): MLoginPaymentDetails {
    return this.paymentService.get();
  }

  get profileUrl(): string {
    return this.configurationService.profileUrl;
  }

  get paymentUrl(): string {
    return this.configurationService.paymentUrl;
  }

  public getTargetApp(): Nullable<string> {
    const products: string[] = this.productsService.get();
    // currently users can only have 1 app, therefore take first product
    return products ? products[0] : null;
  }

  public isAuthenticated(): boolean {
    return this.emailService.get() != null;
  }

  private doLogin(
    code: string,
    codeVerifier: string,
    redirectUri: string,
  ): Observable<AuthorizationResponse> {
    const params: AuthorizationRequest = {
      code,
      codeVerifier,
      redirectUri,
    };
    return this.http.post<AuthorizationResponse>(this.resource, params);
  }

  private doLogout(): Observable<any> {
    return this.http.delete(this.resource);
  }

  private doUpdate(
    productType: string,
    pin: string,
  ): Observable<AuthorizationResponse> {
    const params: UpdateRequest = { productType, pin };
    return this.http.patch<AuthorizationResponse>(this.resource, params);
  }

  public getLocation(): Observable<GeolocationCoordinates> {
    return new Observable((observer: Subscriber<GeolocationCoordinates>) => {
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position: GeolocationPosition) => {
            observer.next(position.coords);
          },
          (error) => {
            observer.error(`Geolocation is not enabled: (code=${error?.code}) ${error?.message}`);
          }, {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 50000,
          },
        );
      } else {
        observer.error('Geolocation not available');
      }
    });
  }


  private navigateToProductHomeOnLoginSuccess(authState: OauthState): void {
    let redirectUrl = authState.savedState.requestUrl as string;
    if (!redirectUrl || redirectUrl === '/') {
      const targetApp = (this.getTargetApp() ?? '').toLowerCase().trim();
      redirectUrl = targetApp ? `/${targetApp}` : '/registration';
    } else if (redirectUrl.includes('/account/')) {
      // If the redirectUrl contains '/account/', redirect to the account page of the target app
      // not to the specific page within the account section. This is to avoid redirecting to a page that the user may not have access to
      // or that page's state may not be valid after login.
      redirectUrl = `${redirectUrl.split('/account/')[0]}/account`;
    }
    this.router.navigateByUrl(redirectUrl, { replaceUrl: true });
  }
}

interface AuthorizationRequest {
  code: string;
  codeVerifier: string;
  redirectUri: string;
}

interface UpdateRequest {
  productType: string;
  pin: string;
}

export interface AuthorizationResponse {
  mLoginId: string;
  emailAddress: string;
  products: string[];
  address: Address;
  line: string;
  mobile: string;
  profile: Profile;
  payment: MLoginPaymentDetails;
}
